
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useNavConfig } from "@/hooks/useNavConfig";
import { X, LogOut, LayoutDashboard } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

interface MobileNavigationProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileNavigation({ isOpen, onClose }: MobileNavigationProps) {
  const { user, signOut, isAdmin } = useAuth();
  const { headerItems, isNavItemVisible } = useNavConfig();

  const handleSignOut = async () => {
    await signOut();
    onClose();
  };

  if (!isOpen) return null;

  const visibleItems = headerItems.filter(isNavItemVisible);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm md:hidden"
        onClick={onClose}
      />

      {/* Drawer — slides in from right */}
      <div className="fixed top-0 right-0 h-full w-[280px] z-50 bg-white flex flex-col shadow-2xl md:hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-brand-primary flex items-center justify-center text-base">
              ⚜️
            </div>
            <span className="text-sm font-bold text-brand-primary font-primary">Clínica Equilíbrio</span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav links — scrollable */}
        <nav className="flex-1 overflow-y-auto py-4 px-4 space-y-1">
          <Link
            to="/inicio"
            onClick={onClose}
            className="flex items-center px-3 py-3 rounded-xl text-gray-700 font-semibold text-[15px] hover:bg-brand-primary/8 hover:text-brand-primary transition-all font-primary"
          >
            Início
          </Link>
          {visibleItems.map(item => (
            <Link
              key={item.id}
              to={item.path}
              onClick={onClose}
              className="flex items-center px-3 py-3 rounded-xl text-gray-700 font-semibold text-[15px] hover:bg-brand-primary/8 hover:text-brand-primary transition-all font-primary"
            >
              {item.label}
            </Link>
          ))}

          {/* Divider + account items when logged in */}
          {user && isAdmin && (
            <>
              <div className="h-px bg-gray-100 my-2 mx-1" />
              <Link
                to="/logins?admin=true"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-600 text-sm hover:bg-gray-50 hover:text-brand-primary transition-all font-primary"
              >
                <LayoutDashboard className="w-4 h-4 shrink-0" /> Painel Administrativo
              </Link>
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-red-500 text-sm hover:bg-red-50 transition-all font-primary font-semibold"
              >
                <LogOut className="w-4 h-4 shrink-0" /> Sair
              </button>
            </>
          )}
        </nav>

        {/* CTA button */}
        <div className="px-4 pb-6 pt-2 border-t border-gray-100">
          <a
            href="https://wa.me/5511998874081"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className="flex items-center justify-center gap-2.5 w-full bg-brand-primary text-white py-3.5 rounded-xl font-bold text-sm hover:bg-brand-primary/90 transition-all font-primary"
          >
            <FaWhatsapp size={18} />
            Agendar Consulta
          </a>
        </div>
      </div>
    </>
  );
}
